import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { AlertTriangle, Loader2, XCircle, RefreshCw, Trash2 } from 'lucide-react';
import { getConnectorAgents } from '@/lib/connector-api';
import type { Connector } from '@/lib/connector-types';

interface ConnectorDeletionDialogProps {
  connector: Connector;
  onConfirmDelete: () => void;
  onCancel: () => void;
}

export function ConnectorDeletionDialog({
  connector,
  onConfirmDelete,
  onCancel,
}: ConnectorDeletionDialogProps) {
  const [agents, setAgents] = useState<{ agentId: string; agentName: string }[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [confirmed, setConfirmed] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const fetchAgents = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await getConnectorAgents(connector.id);
      setAgents(result.agents ?? []);
    } catch (err: any) {
      setError(err.message || 'Failed to check dependencies');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAgents();
  }, [connector.id]);

  const handleDelete = () => {
    setDeleting(true);
    onConfirmDelete();
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-background rounded-lg shadow-lg border w-full max-w-sm overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center gap-2.5 px-5 py-4 border-b">
          <div className="flex items-center justify-center size-8 rounded-lg bg-destructive/10">
            <Trash2 className="size-4 text-destructive" />
          </div>
          <div>
            <h2 className="text-sm font-semibold leading-tight">Delete Connector</h2>
            <p className="text-xs text-muted-foreground truncate max-w-[220px]">{connector.name}</p>
          </div>
        </div>

        {/* Body */}
        <div className="px-5 py-4">
          {loading && (
            <div className="flex flex-col items-center justify-center py-8">
              <Loader2 className="size-5 text-primary animate-spin mb-2" />
              <p className="text-xs text-muted-foreground">Checking dependencies...</p>
            </div>
          )}

          {!loading && error && (
            <div className="space-y-3">
              <div className="flex flex-col items-center justify-center py-4">
                <div className="flex items-center justify-center size-10 rounded-xl bg-destructive/10 mb-3">
                  <XCircle className="size-5 text-destructive" />
                </div>
                <p className="text-xs text-muted-foreground text-center">
                  Could not check dependencies. Please try again.
                </p>
              </div>
              <div className="flex items-start gap-2 bg-destructive/10 text-destructive text-[10px] p-2.5 rounded-md border border-destructive/20">
                <XCircle className="size-3 shrink-0 mt-0.5" />
                <span>{error}</span>
              </div>
            </div>
          )}

          {!loading && !error && agents && agents.length > 0 && (
            <div className="space-y-3">
              <div className="flex items-start gap-2 bg-destructive/10 text-xs p-3 rounded-md border border-destructive/20">
                <AlertTriangle className="size-3.5 text-destructive shrink-0 mt-0.5" />
                <span>
                  This connector is linked to{' '}
                  <span className="font-semibold">
                    {agents.length} agent{agents.length !== 1 ? 's' : ''}
                  </span>. Deleting it will remove its tools from those agents.
                </span>
              </div>

              <ScrollArea className="max-h-28 rounded-md border bg-card">
                <div className="p-2 space-y-1">
                  {agents.map((agent) => (
                    <div key={agent.agentId} className="flex items-center gap-2 px-2 py-1 rounded text-xs">
                      <div className="size-1.5 rounded-full bg-primary shrink-0" />
                      <span className="font-medium truncate">{agent.agentName}</span>
                    </div>
                  ))}
                </div>
              </ScrollArea>

              <label className="flex items-start gap-2.5 cursor-pointer rounded-md border p-3 hover:bg-muted/40 transition-colors">
                <input
                  type="checkbox"
                  checked={confirmed}
                  onChange={(e) => setConfirmed(e.target.checked)}
                  className="mt-0.5 accent-destructive"
                />
                <span className="text-xs leading-relaxed">
                  I understand this will remove the connector from all linked agents.
                </span>
              </label>
            </div>
          )}

          {!loading && !error && agents && agents.length === 0 && (
            <div className="space-y-2">
              <p className="text-xs text-muted-foreground">
                This connector is not linked to any agents. You can safely delete it.
              </p>
              <p className="text-xs">
                Are you sure you want to delete <span className="font-medium">"{connector.name}"</span>?
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t bg-muted/30">
          {!loading && error ? (
            <>
              <Button variant="outline" size="sm" onClick={onCancel}>Cancel</Button>
              <Button size="sm" onClick={fetchAgents}>
                <RefreshCw className="size-3.5 mr-1.5" />
                Retry
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline" size="sm" onClick={onCancel} disabled={deleting}>Cancel</Button>
              <Button
                variant="destructive"
                size="sm"
                onClick={handleDelete}
                disabled={loading || deleting || (agents !== null && agents.length > 0 && !confirmed)}
              >
                {deleting ? (
                  <>
                    <Loader2 className="size-3.5 mr-1.5 animate-spin" />
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 className="size-3.5 mr-1.5" />
                    Delete
                  </>
                )}
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
